import { RadioGroup } from '../../engine/fields/RadioGroup';
import { FieldRow } from '../../engine/fields/FieldRow';
import { useIrsLimits } from '../../engine/state/CalculatorProvider';
import type { HsaSavingsValues } from './config';

/**
 * A custom step item (not a declarative field) because switching plan type
 * carries a side effect the generic field contract doesn't model: hsa-
 * savings-calc.js re-clamps contributionAmt/medicalExpenses when flipping
 * family -> individual, since individual limits are always <= family ones.
 * individual -> family never needs this. Kept calculator-local per
 * docs/calculator-engine-architecture.md §4.2 (the `custom` escape hatch is
 * for exactly one odd control inside an otherwise normal step).
 */
type SetValue = <K extends keyof HsaSavingsValues>(name: K, value: HsaSavingsValues[K]) => void;

/**
 * Plan-type change with its side effect (see above). Shared by the legacy
 * radios below and Spotlight's plan-type gate.
 */
export function usePlanTypeChange(values: HsaSavingsValues, setValue: SetValue) {
  const limits = useIrsLimits();
  const hsa = limits.current.hsa;

  return (next: HsaSavingsValues['planType']) => {
    const wasFamily = values.planType === 'family';
    setValue('planType', next);

    if (next === 'individual' && wasFamily) {
      const newContributionLimit = hsa.maxContributionSingle + hsa.catchupSingle;
      const newMaxOutOfPocket = hsa.maxOutOfPocketSingle;
      if ((values.contributionAmt ?? 0) > newContributionLimit) {
        setValue('contributionAmt', newContributionLimit);
      }
      if ((values.medicalExpenses ?? 0) > newMaxOutOfPocket) {
        setValue('medicalExpenses', newMaxOutOfPocket);
      }
    }
  };
}

export function PlanTypeField({ values, setValue }: { values: HsaSavingsValues; setValue: SetValue }) {
  const changePlanType = usePlanTypeChange(values, setValue);
  const handleChange = (value: string | number) => changePlanType(value as HsaSavingsValues['planType']);

  // Namespaced by calculator id, not just the bare field name — a native
  // <input name="..."> radio group is scoped to the whole document, not to
  // a React subtree, so an unqualified name risks colliding with another
  // calculator's same-named field if both are ever mounted on one page
  // (confirmed live: three calculators had each independently picked
  // "planType"). See renderItem.tsx's FieldItem for the full writeup.
  const id = 'hsab-calc-field-hsa-savings-planType';

  return (
    <FieldRow id={id} label="Choose your plan type:" error={null}>
      <RadioGroup
        id={id}
        name={id}
        value={values.planType}
        options={[
          { label: 'Individual', value: 'individual' },
          { label: 'Family', value: 'family' },
        ]}
        layout="inline"
        onChange={handleChange}
        onBlur={() => {}}
        error={null}
      />
    </FieldRow>
  );
}
